/**
 * Navigation State Manager
 * Manages navigation history and slide menu state for proper back navigation handling
 */

import { reportError } from '../sentry';

const NAVIGATION_STATE_KEY = 'app_navigation_state';
const SLIDE_MENU_STATE_KEY = 'slide_menu_was_open';
const SEARCH_SCREEN_STATE_KEY = 'search_screen_was_open';
const SEARCH_QUERY_KEY = 'search_screen_query';

/**
 * Save the current navigation state including slide menu status
 * @param {boolean} slideMenuOpen - Whether the slide menu is currently open
 */
export const saveNavigationState = (slideMenuOpen = false) => {
  try {
    const state = {
      slideMenuWasOpen: slideMenuOpen,
      timestamp: Date.now()
    };
    sessionStorage.setItem(NAVIGATION_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    reportError(error, 'save navigation state');
  }
};

/**
 * Get the saved navigation state
 * @returns {Object} The saved navigation state
 */
export const getNavigationState = () => {
  try {
    const stateStr = sessionStorage.getItem(NAVIGATION_STATE_KEY);
    if (!stateStr) {
      return { slideMenuWasOpen: false };
    }
    const state = JSON.parse(stateStr);
    // Clear state older than 5 minutes to avoid stale data
    if (Date.now() - state.timestamp > 300000) {
      clearNavigationState();
      return { slideMenuWasOpen: false };
    }
    return state;
  } catch (error) {
    reportError(error, 'get navigation state');
    return { slideMenuWasOpen: false };
  }
};

/**
 * Clear the navigation state including slide menu state
 */
export const clearNavigationState = () => {
  try {
    sessionStorage.removeItem(NAVIGATION_STATE_KEY);
    sessionStorage.removeItem(SLIDE_MENU_STATE_KEY);
    sessionStorage.removeItem(SEARCH_SCREEN_STATE_KEY);
    sessionStorage.removeItem(SEARCH_QUERY_KEY);
  } catch (error) {
    reportError(error, 'clear navigation state');
  }
};

/**
 * Mark that the slide menu was open before navigation
 */
export const markSlideMenuOpen = () => {
  try {
    sessionStorage.setItem(SLIDE_MENU_STATE_KEY, 'true');
  } catch (error) {
    reportError(error, 'mark slide menu open');
  }
};

/**
 * Check if the slide menu was open before navigation and clear the flag
 * @returns {boolean} Whether the slide menu was open
 */
export const wasSlideMenuOpen = () => {
  try {
    const wasOpen = sessionStorage.getItem(SLIDE_MENU_STATE_KEY) === 'true';
    sessionStorage.removeItem(SLIDE_MENU_STATE_KEY);
    return wasOpen;
  } catch (error) {
    reportError(error, 'check slide menu state');
    return false;
  }
};

/**
 * Mark that the search screen was open before navigation
 * @param {string} searchQuery - The search query to save
 */
export const markSearchScreenOpen = (searchQuery = '') => {
  try {
    sessionStorage.setItem(SEARCH_SCREEN_STATE_KEY, 'true');
    if (searchQuery) {
      sessionStorage.setItem(SEARCH_QUERY_KEY, searchQuery);
    }
  } catch (error) {
    reportError(error, 'mark search screen open');
  }
};

/**
 * Check if the search screen was open before navigation and clear the flag
 * @returns {boolean} Whether the search screen was open
 */
export const wasSearchScreenOpen = () => {
  try {
    const wasOpen = sessionStorage.getItem(SEARCH_SCREEN_STATE_KEY) === 'true';
    sessionStorage.removeItem(SEARCH_SCREEN_STATE_KEY);
    return wasOpen;
  } catch (error) {
    reportError(error, 'check search screen state');
    return false;
  }
};

/**
 * Get the saved search query and clear it
 * @returns {string} The saved search query
 */
export const getSavedSearchQuery = () => {
  try {
    const query = sessionStorage.getItem(SEARCH_QUERY_KEY) || '';
    sessionStorage.removeItem(SEARCH_QUERY_KEY);
    return query;
  } catch (error) {
    reportError(error, 'get saved search query');
    return '';
  }
};

